<template>
  <div class="landing-page">
    <!-- Simplified Header -->
    <header class="header">
      <div class="header-container">
        <div class="logo">
          <img
            src="@/assets/optionsTechnology-logo.svg"
            alt="Options IT Logo"
          />
        </div>
        <button @click="login" class="login-button">
          <svg
            class="login-icon"
            width="16"
            height="16"
            viewBox="0 0 16 16"
            fill="none"
          >
            <path
              d="M13 5L13 3C13 1.89543 12.1046 1 11 1L3 1C1.89543 1 1 1.89543 1 3L1 13C1 14.1046 1.89543 15 3 15L11 15C12.1046 15 13 14.1046 13 13L13 11"
              stroke="currentColor"
              stroke-width="1.5"
              stroke-linecap="round"
            />
            <path
              d="M10 8L15 8M15 8L12.5 5.5M15 8L12.5 10.5"
              stroke="currentColor"
              stroke-width="1.5"
              stroke-linecap="round"
              stroke-linejoin="round"
            />
          </svg>
          Sign In
        </button>
      </div>
    </header>

    <main class="main">
      <!-- Hero Section -->
      <section class="hero">
        <div class="hero-background">
          <div class="hero-gradient"></div>
          <div class="hero-pattern"></div>
        </div>
        <div class="hero-content">
          <h1 class="hero-title">
            Welcome to <span class="gradient-text">Cyanic Cloud</span>
          </h1>
          <p class="hero-subtitle">
            Enterprise-grade Microsoft 365 management platform for modern
            organisations
          </p>
          <div class="hero-stats">
            <div class="stat">
              <span class="stat-value">500+</span>
              <span class="stat-label">Security Policies</span>
            </div>
            <div class="stat">
              <span class="stat-value">99.9%</span>
              <span class="stat-label">Compliance Rate</span>
            </div>
            <div class="stat">
              <span class="stat-value">24/7</span>
              <span class="stat-label">Monitoring</span>
            </div>
          </div>
        </div>
      </section>

      <!-- Features Section -->
      <section id="features" class="features">
        <div class="container">
          <div class="section-header">
            <h2 class="section-title">Powerful Features</h2>
            <p class="section-subtitle">
              Everything you need to manage and secure your Microsoft 365
              environment
            </p>
          </div>

          <div class="feature-grid">
            <div class="feature-card">
              <div class="feature-icon security">
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                  <path
                    d="M12 2L4 7V11C4 16.52 7.34 21.44 12 22.5C16.66 21.44 20 16.52 20 11V7L12 2Z"
                    stroke="currentColor"
                    stroke-width="2"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                  />
                  <path
                    d="M9 12L11 14L15 10"
                    stroke="currentColor"
                    stroke-width="2"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                  />
                </svg>
              </div>
              <h3>Security & Compliance</h3>
              <p>
                Deploy entire environments from existing configurations. Copy
                and move individual configurations between tenants. Push changes
                to tenants in bulk to save time and eliminate mistakes.
              </p>
              <div class="feature-highlights">
                <span class="highlight">Automated Deployment</span>
                <span class="highlight">Bulk Operations</span>
                <span class="highlight">Policy Management</span>
              </div>
            </div>

            <div class="feature-card">
              <div class="feature-icon packaging">
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                  <path
                    d="M21 16V8C20.9996 7.64927 20.9071 7.30481 20.7315 7.00116C20.556 6.69751 20.3037 6.44536 20 6.27L13 2.27C12.696 2.09446 12.3511 2.00205 12 2.00205C11.6489 2.00205 11.304 2.09446 11 2.27L4 6.27C3.69626 6.44536 3.44398 6.69751 3.26846 7.00116C3.09294 7.30481 3.00036 7.64927 3 8V16C3.00036 16.3507 3.09294 16.6952 3.26846 16.9988C3.44398 17.3025 3.69626 17.5546 4 17.73L11 21.73C11.304 21.9055 11.6489 21.9979 12 21.9979C12.3511 21.9979 12.696 21.9055 13 21.73L20 17.73C20.3037 17.5546 20.556 17.3025 20.7315 16.9988C20.9071 16.6952 20.9996 16.3507 21 16Z"
                    stroke="currentColor"
                    stroke-width="2"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                  />
                  <path
                    d="M3.27 6.96L12 12.01L20.73 6.96"
                    stroke="currentColor"
                    stroke-width="2"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                  />
                  <path
                    d="M12 22.08V12"
                    stroke="currentColor"
                    stroke-width="2"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                  />
                </svg>
              </div>
              <h3>Enterprise Application Packaging</h3>
              <p>
                Easily create and manage Intune applications. Update and deploy
                new versions via automation. Deploy application packages to
                tenants in bulk efficiently.
              </p>
              <div class="feature-highlights">
                <span class="highlight">Intune Integration</span>
                <span class="highlight">Version Control</span>
                <span class="highlight">Automated Updates</span>
              </div>
            </div>

            <div class="feature-card">
              <div class="feature-icon monitoring">
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                  <path
                    d="M22 12H18L15 21L9 3L6 12H2"
                    stroke="currentColor"
                    stroke-width="2"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                  />
                </svg>
              </div>
              <h3>Monitoring & Reporting</h3>
              <p>
                Programmatically document and track changes to configurations
                with comprehensive reporting and real-time monitoring
                capabilities.
              </p>
              <div class="feature-highlights">
                <span class="highlight">Real-time Tracking</span>
                <span class="highlight">Change Logs</span>
                <span class="highlight">Custom Reports</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      <!-- Benefits Section -->
      <section class="benefits">
        <div class="container">
          <div class="benefits-grid">
            <div class="benefits-content">
              <h2 class="section-title">Why Choose Cyanic Cloud?</h2>
              <div class="benefit-list">
                <div class="benefit-item">
                  <div class="benefit-icon">✓</div>
                  <div>
                    <h4>Enterprise Ready</h4>
                    <p>
                      Built for scale with enterprise-grade security and
                      reliability
                    </p>
                  </div>
                </div>
                <div class="benefit-item">
                  <div class="benefit-icon">✓</div>
                  <div>
                    <h4>Time Saving</h4>
                    <p>
                      Automate repetitive tasks and reduce manual configuration
                      errors
                    </p>
                  </div>
                </div>
                <div class="benefit-item">
                  <div class="benefit-icon">✓</div>
                  <div>
                    <h4>Compliance Focused</h4>
                    <p>
                      Stay compliant with industry standards and best practices
                    </p>
                  </div>
                </div>
                <div class="benefit-item">
                  <div class="benefit-icon">✓</div>
                  <div>
                    <h4>Expert Support</h4>
                    <p>Dedicated support team to help you succeed</p>
                  </div>
                </div>
              </div>
            </div>
            <div class="benefits-visual">
              <div class="visual-card">
                <div class="visual-stat">
                  <span class="visual-number">85%</span>
                  <span class="visual-label">Reduction in deployment time</span>
                </div>
                <div class="visual-stat">
                  <span class="visual-number">100%</span>
                  <span class="visual-label">Policy compliance achieved</span>
                </div>
                <div class="visual-stat">
                  <span class="visual-number">50+</span>
                  <span class="visual-label">Hours saved monthly</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <!-- Contact Section -->
      <section id="contact" class="contact">
        <div class="container">
          <div class="contact-wrapper">
            <div class="contact-info">
              <h2 class="section-title">Get in Touch</h2>
              <p class="contact-description">
                Ready to transform your Microsoft 365 management? Our team is
                here to help you get started.
              </p>
              <div class="contact-details">
                <div class="contact-item">
                  <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                    <path
                      d="M2 5C2 3.89543 2.89543 3 4 3H16C17.1046 3 18 3.89543 18 5V15C18 16.1046 17.1046 17 16 17H4C2.89543 17 2 16.1046 2 15V5Z"
                      stroke="currentColor"
                      stroke-width="1.5"
                    />
                    <path
                      d="M2 5L10 11L18 5"
                      stroke="currentColor"
                      stroke-width="1.5"
                      stroke-linecap="round"
                      stroke-linejoin="round"
                    />
                  </svg>
                  <span>support@cyaniccloud.com</span>
                </div>
                <div class="contact-item">
                  <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                    <path
                      d="M3 5C3 3.89543 3.89543 3 5 3H8L9 6H15C16.1046 6 17 6.89543 17 8V15C17 16.1046 16.1046 17 15 17H5C3.89543 17 3 16.1046 3 15V5Z"
                      stroke="currentColor"
                      stroke-width="1.5"
                    />
                  </svg>
                  <span>Enterprise solutions available</span>
                </div>
              </div>
            </div>

            <form class="contact-form" @submit.prevent="handleSubmit">
              <div class="form-group">
                <input
                  type="text"
                  placeholder="Your Name"
                  class="form-input"
                  v-model="formData.name"
                  required
                />
              </div>
              <div class="form-group">
                <input
                  type="email"
                  placeholder="Your Email"
                  class="form-input"
                  v-model="formData.email"
                  required
                />
              </div>
              <div class="form-group">
                <textarea
                  placeholder="How can we help you?"
                  class="form-input form-textarea"
                  rows="5"
                  v-model="formData.message"
                  required
                ></textarea>
              </div>
              <button type="submit" class="submit-button">
                Send Message
                <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                  <path
                    d="M6 12L10 8L6 4"
                    stroke="currentColor"
                    stroke-width="2"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                  />
                </svg>
              </button>
            </form>
          </div>
        </div>
      </section>
    </main>

    <!-- Footer -->
    <footer class="footer">
      <div class="footer-container">
        <div class="footer-content">
          <div class="footer-brand">
            <img
              src="@/assets/optionsTechnology-logo.svg"
              alt="Options IT"
              class="footer-logo"
            />
            <p class="footer-description">
              Enterprise Microsoft 365 Management Platform
            </p>
          </div>
          <div class="footer-links">
            <div class="footer-column">
              <h4>Product</h4>
              <a href="#features">Features</a>
              <a href="#security">Security</a>
              <a href="#compliance">Compliance</a>
            </div>
            <div class="footer-column">
              <h4>Company</h4>
              <a href="#about">About Us</a>
              <a href="#contact">Contact</a>
              <a href="#support">Support</a>
            </div>
            <div class="footer-column">
              <h4>Resources</h4>
              <a href="#docs">Documentation</a>
              <a href="#api">API Reference</a>
              <a href="#status">System Status</a>
            </div>
          </div>
        </div>
        <div class="footer-bottom">
          <p>&copy; 2024 Options IT. All rights reserved.</p>
          <div class="footer-legal">
            <a href="#privacy">Privacy Policy</a>
            <span>•</span>
            <a href="#terms">Terms of Service</a>
          </div>
        </div>
      </div>
    </footer>

    <!-- Thank You Modal -->
    <transition name="modal-fade">
      <div v-if="showThankYouModal" class="modal-overlay" @click="closeModal">
        <div class="modal-container" @click.stop>
          <div class="modal-header">
            <div class="modal-icon-wrapper">
              <svg
                class="modal-icon"
                width="32"
                height="32"
                viewBox="0 0 32 32"
                fill="none"
              >
                <circle
                  cx="16"
                  cy="16"
                  r="16"
                  fill="url(#gradient)"
                  fill-opacity="0.1"
                />
                <path
                  d="M11 16L14 19L21 12"
                  stroke="url(#gradient)"
                  stroke-width="2.5"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                />
                <defs>
                  <linearGradient
                    id="gradient"
                    x1="0%"
                    y1="0%"
                    x2="100%"
                    y2="100%"
                  >
                    <stop
                      offset="0%"
                      style="stop-color: #10b981; stop-opacity: 1"
                    />
                    <stop
                      offset="100%"
                      style="stop-color: #059669; stop-opacity: 1"
                    />
                  </linearGradient>
                </defs>
              </svg>
            </div>
          </div>

          <div class="modal-content">
            <h3 class="modal-title">Thank You!</h3>
            <p class="modal-message">
              Thank you for your message. We'll get back to you soon!
            </p>
            <p class="modal-submessage">
              A member of our team will respond within 24 business hours.
            </p>
          </div>

          <div class="modal-footer">
            <button class="modal-button" @click="closeModal">OK</button>
          </div>
        </div>
      </div>
    </transition>
  </div>
</template>

<script>
import { msalInstance } from "@/auth.js";
import store from "@/store";
import defaultProfilePhoto from "@/assets/Default-Profile-Picture.png";

export default {
  name: "ModernLandingPage",
  data() {
    return {
      showThankYouModal: false,
      formData: {
        name: "",
        email: "",
        message: "",
      },
    };
  },
  methods: {
    async login() {
      try {
        await msalInstance.handleRedirectPromise();
        let currentAccount = msalInstance.getActiveAccount();

        if (!currentAccount) {
          const loginResponse = await msalInstance.loginPopup({
            scopes: [
              "openid",
              "profile",
              "email",
              "offline_access",
              "User.Read",
              "499b84ac-1321-427f-aa17-267ca6975798/user_impersonation",
            ],
          });
          currentAccount = loginResponse.account;
        }

        if (currentAccount) {
          msalInstance.setActiveAccount(currentAccount);
          store.commit("setLoggedIn", true);
          store.commit("setLoggedInUser", {
            displayName: currentAccount.name,
            upn: currentAccount.username,
          });
          await this.fetchUserProfilePhoto();
          this.$router.push("/azure-ado-projects");
        }
      } catch (error) {
        console.error("Login error:", error);
      }
    },

    async fetchUserProfilePhoto() {
      try {
        const token = await msalInstance.acquireTokenSilent({
          scopes: ["User.Read"],
          account: msalInstance.getActiveAccount(),
        });

        const response = await fetch(
          `https://graph.microsoft.com/v1.0/me/photo/$value`,
          {
            headers: {
              Authorization: `Bearer ${token.accessToken}`,
            },
          }
        );

        if (response.ok) {
          const blob = await response.blob();
          const reader = new FileReader();
          reader.onload = () => {
            store.commit("setUserPhoto", reader.result);
          };
          reader.readAsDataURL(blob);
        } else {
          store.commit("setUserPhoto", defaultProfilePhoto);
        }
      } catch (error) {
        console.error("Error fetching profile photo:", error);
        store.commit("setUserPhoto", defaultProfilePhoto);
      }
    },

    handleSubmit() {
      // Show the thank you modal
      this.showThankYouModal = true;

      // Reset form after submission
      this.formData = {
        name: "",
        email: "",
        message: "",
      };

      // Here you could also send the form data to your backend
      // Example: await this.sendFormData(this.formData);
    },

    closeModal() {
      this.showThankYouModal = false;
    },
  },
};
</script>

<style scoped>
@import url("https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700;800&display=swap");

* {
  box-sizing: border-box;
  margin: 0;
  padding: 0;
}

.landing-page {
  font-family: "Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto,
    sans-serif;
  line-height: 1.6;
  color: #1e293b;
  background: #ffffff;
  overflow-x: hidden;
}

/* Header */
.header {
  background: #ffffff;
  border-bottom: 1px solid #e2e8f0;
  position: sticky;
  top: 0;
  z-index: 100;
  backdrop-filter: blur(10px);
  background: rgba(255, 255, 255, 0.95);
}

.header-container {
  max-width: 1280px;
  margin: 0 auto;
  padding: 1rem 2rem;
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.logo img {
  height: 40px;
  width: auto;
}

.login-button {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.625rem 1.25rem;
  background: #3b82f6;
  color: white;
  border: none;
  border-radius: 0.5rem;
  font-size: 0.875rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
  font-family: inherit;
}

.login-button:hover {
  background: #2563eb;
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(59, 130, 246, 0.25);
}

.login-icon {
  width: 1rem;
  height: 1rem;
}

/* Hero Section */
.hero {
  position: relative;
  padding: 6rem 2rem;
  min-height: 500px;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  background: linear-gradient(135deg, #1e293b 0%, #334155 100%);
}

.hero-background {
  position: absolute;
  inset: 0;
  z-index: 0;
}

.hero-gradient {
  position: absolute;
  inset: 0;
  background: linear-gradient(135deg, #3b82f6 0%, #8b5cf6 100%);
  opacity: 0.1;
}

.hero-pattern {
  position: absolute;
  inset: 0;
  background-image: radial-gradient(
      circle at 20% 80%,
      rgba(59, 130, 246, 0.1) 0%,
      transparent 50%
    ),
    radial-gradient(
      circle at 80% 20%,
      rgba(139, 92, 246, 0.1) 0%,
      transparent 50%
    );
}

.hero-content {
  position: relative;
  max-width: 900px;
  margin: 0 auto;
  text-align: center;
  z-index: 1;
}

.hero-title {
  font-size: 3.5rem;
  font-weight: 800;
  line-height: 1.1;
  margin-bottom: 1.5rem;
  color: #f8fafc;
}

.gradient-text {
  background: linear-gradient(135deg, #60a5fa 0%, #a78bfa 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
}

.hero-subtitle {
  font-size: 1.25rem;
  color: #94a3b8;
  margin-bottom: 3rem;
  max-width: 600px;
  margin-left: auto;
  margin-right: auto;
}

.hero-stats {
  display: flex;
  justify-content: center;
  gap: 3rem;
  flex-wrap: wrap;
}

.stat {
  display: flex;
  flex-direction: column;
  align-items: center;
}

.stat-value {
  font-size: 2.5rem;
  font-weight: 700;
  color: #60a5fa;
  line-height: 1;
}

.stat-label {
  font-size: 0.875rem;
  color: #94a3b8;
  margin-top: 0.5rem;
}

/* Container */
.container {
  max-width: 1280px;
  margin: 0 auto;
  padding: 0 2rem;
}

/* Features Section */
.features {
  padding: 5rem 0;
  background: #f8fafc;
}

.section-header {
  text-align: center;
  margin-bottom: 4rem;
}

.section-title {
  font-size: 2.5rem;
  font-weight: 700;
  color: #1e293b;
  margin-bottom: 1rem;
}

.section-subtitle {
  font-size: 1.125rem;
  color: #64748b;
  max-width: 600px;
  margin: 0 auto;
}

.feature-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(350px, 1fr));
  gap: 2rem;
}

.feature-card {
  background: white;
  padding: 2rem;
  border-radius: 1rem;
  border: 1px solid #e2e8f0;
  transition: all 0.3s;
}

.feature-card:hover {
  transform: translateY(-4px);
  box-shadow: 0 20px 40px rgba(0, 0, 0, 0.08);
  border-color: #cbd5e1;
}

.feature-icon {
  width: 3rem;
  height: 3rem;
  border-radius: 0.75rem;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 1.5rem;
}

.feature-icon.security {
  background: linear-gradient(135deg, #10b981 0%, #059669 100%);
  color: white;
}

.feature-icon.packaging {
  background: linear-gradient(135deg, #3b82f6 0%, #2563eb 100%);
  color: white;
}

.feature-icon.monitoring {
  background: linear-gradient(135deg, #8b5cf6 0%, #7c3aed 100%);
  color: white;
}

.feature-card h3 {
  font-size: 1.25rem;
  font-weight: 600;
  color: #1e293b;
  margin-bottom: 0.75rem;
}

.feature-card p {
  color: #64748b;
  line-height: 1.6;
  margin-bottom: 1.5rem;
}

.feature-highlights {
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
}

.highlight {
  font-size: 0.75rem;
  padding: 0.25rem 0.75rem;
  background: #f1f5f9;
  color: #475569;
  border-radius: 9999px;
  font-weight: 500;
}

/* Benefits Section */
.benefits {
  padding: 5rem 0;
  background: white;
}

.benefits-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 4rem;
  align-items: center;
}

.benefit-list {
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
  margin-top: 2rem;
}

.benefit-item {
  display: flex;
  gap: 1rem;
  align-items: flex-start;
}

.benefit-icon {
  width: 2rem;
  height: 2rem;
  background: linear-gradient(135deg, #10b981 0%, #059669 100%);
  color: white;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  font-weight: bold;
}

.benefit-item h4 {
  font-size: 1.125rem;
  font-weight: 600;
  color: #1e293b;
  margin-bottom: 0.25rem;
}

.benefit-item p {
  color: #64748b;
  font-size: 0.875rem;
}

.visual-card {
  background: linear-gradient(135deg, #f0f9ff 0%, #e0f2fe 100%);
  padding: 2.5rem;
  border-radius: 1rem;
  border: 1px solid #bfdbfe;
}

.visual-stat {
  display: flex;
  flex-direction: column;
  margin-bottom: 2rem;
}

.visual-stat:last-child {
  margin-bottom: 0;
}

.visual-number {
  font-size: 2.5rem;
  font-weight: 700;
  color: #3b82f6;
  margin-bottom: 0.5rem;
}

.visual-label {
  color: #64748b;
  font-size: 0.875rem;
}

/* Contact Section */
.contact {
  padding: 5rem 0;
  background: #f8fafc;
}

.contact-wrapper {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 4rem;
  align-items: start;
}

.contact-description {
  color: #64748b;
  margin: 1rem 0 2rem;
  line-height: 1.6;
}

.contact-details {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.contact-item {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  color: #475569;
}

.contact-item svg {
  color: #3b82f6;
}

.contact-form {
  background: white;
  padding: 2rem;
  border-radius: 1rem;
  border: 1px solid #e2e8f0;
}

.form-group {
  margin-bottom: 1.5rem;
}

.form-input {
  width: 100%;
  padding: 0.75rem 1rem;
  border: 1px solid #e2e8f0;
  border-radius: 0.5rem;
  font-size: 0.875rem;
  transition: all 0.2s;
  font-family: inherit;
  background: #ffffff;
}

.form-input:focus {
  outline: none;
  border-color: #3b82f6;
  box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
}

.form-textarea {
  resize: vertical;
  min-height: 120px;
}

.submit-button {
  width: 100%;
  padding: 0.75rem 1.5rem;
  background: linear-gradient(135deg, #3b82f6 0%, #2563eb 100%);
  color: white;
  border: none;
  border-radius: 0.5rem;
  font-size: 0.875rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  font-family: inherit;
}

.submit-button:hover {
  transform: translateY(-2px);
  box-shadow: 0 8px 16px rgba(59, 130, 246, 0.25);
}

/* Footer */
.footer {
  background: #0f172a;
  color: #94a3b8;
  padding: 3rem 0 1.5rem;
}

.footer-container {
  max-width: 1280px;
  margin: 0 auto;
  padding: 0 2rem;
}

.footer-content {
  display: grid;
  grid-template-columns: 2fr 3fr;
  gap: 4rem;
  margin-bottom: 3rem;
}

.footer-logo {
  height: 32px;
  width: auto;
  margin-bottom: 1rem;
  filter: brightness(0) invert(1);
}

.footer-description {
  font-size: 0.875rem;
  color: #64748b;
}

.footer-links {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 2rem;
}

.footer-column h4 {
  font-size: 0.875rem;
  font-weight: 600;
  color: #e2e8f0;
  margin-bottom: 1rem;
}

.footer-column a {
  display: block;
  color: #94a3b8;
  text-decoration: none;
  font-size: 0.875rem;
  margin-bottom: 0.75rem;
  transition: color 0.2s;
}

.footer-column a:hover {
  color: #3b82f6;
}

.footer-bottom {
  padding-top: 2rem;
  border-top: 1px solid #1e293b;
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-size: 0.875rem;
}

.footer-legal {
  display: flex;
  gap: 1rem;
  align-items: center;
}

.footer-legal a {
  color: #94a3b8;
  text-decoration: none;
  transition: color 0.2s;
}

.footer-legal a:hover {
  color: #3b82f6;
}

/* Responsive Design */
@media (max-width: 1024px) {
  .hero-title {
    font-size: 2.5rem;
  }

  .benefits-grid,
  .contact-wrapper {
    grid-template-columns: 1fr;
    gap: 3rem;
  }
}

@media (max-width: 768px) {
  .header-container {
    padding: 1rem;
  }

  .hero {
    padding: 4rem 1rem;
  }

  .hero-title {
    font-size: 2rem;
  }

  .hero-subtitle {
    font-size: 1rem;
  }

  .hero-stats {
    gap: 2rem;
  }

  .stat-value {
    font-size: 2rem;
  }

  .section-title {
    font-size: 2rem;
  }

  .feature-grid {
    grid-template-columns: 1fr;
    gap: 1.5rem;
  }

  .footer-content {
    grid-template-columns: 1fr;
    gap: 2rem;
  }

  .footer-links {
    grid-template-columns: 1fr;
    gap: 1.5rem;
  }

  .footer-bottom {
    flex-direction: column;
    gap: 1rem;
    text-align: center;
  }
}

@media (max-width: 480px) {
  .hero-title {
    font-size: 1.75rem;
  }

  .login-button {
    padding: 0.5rem 1rem;
    font-size: 0.8125rem;
  }
}

/* Modal Styles */
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  backdrop-filter: blur(4px);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: 1rem;
}

.modal-container {
  background: white;
  border-radius: 1rem;
  max-width: 400px;
  width: 100%;
  box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.1),
    0 10px 10px -5px rgba(0, 0, 0, 0.04);
  overflow: hidden;
  transform: translateY(0);
  transition: all 0.3s ease;
}

.modal-header {
  padding: 2rem 2rem 1rem;
  display: flex;
  justify-content: center;
}

.modal-icon-wrapper {
  width: 64px;
  height: 64px;
  background: linear-gradient(135deg, #10b981 0%, #059669 100%);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
  animation: pulse 2s infinite;
}

.modal-icon {
  filter: brightness(0) invert(1);
}

@keyframes pulse {
  0% {
    box-shadow: 0 0 0 0 rgba(16, 185, 129, 0.4);
  }
  70% {
    box-shadow: 0 0 0 15px rgba(16, 185, 129, 0);
  }
  100% {
    box-shadow: 0 0 0 0 rgba(16, 185, 129, 0);
  }
}

.modal-content {
  padding: 0 2rem 2rem;
  text-align: center;
}

.modal-title {
  font-size: 1.5rem;
  font-weight: 700;
  color: #1e293b;
  margin-bottom: 0.5rem;
}

.modal-message {
  font-size: 1rem;
  color: #475569;
  margin-bottom: 0.5rem;
  line-height: 1.5;
}

.modal-submessage {
  font-size: 0.875rem;
  color: #64748b;
  line-height: 1.5;
}

.modal-footer {
  padding: 1.5rem 2rem;
  background: #f8fafc;
  border-top: 1px solid #e2e8f0;
  display: flex;
  justify-content: center;
}

.modal-button {
  padding: 0.625rem 2rem;
  background: #3b82f6;
  color: white;
  border: none;
  border-radius: 0.5rem;
  font-size: 0.875rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
  font-family: inherit;
  min-width: 100px;
}

.modal-button:hover {
  background: #2563eb;
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(59, 130, 246, 0.25);
}

/* Modal Fade Transition */
.modal-fade-enter-active,
.modal-fade-leave-active {
  transition: opacity 0.3s ease;
}

.modal-fade-enter-from,
.modal-fade-leave-to {
  opacity: 0;
}

.modal-fade-enter-active .modal-container {
  animation: modal-slide-in 0.3s ease;
}

.modal-fade-leave-active .modal-container {
  animation: modal-slide-out 0.3s ease;
}

@keyframes modal-slide-in {
  from {
    transform: translateY(-20px);
    opacity: 0;
  }
  to {
    transform: translateY(0);
    opacity: 1;
  }
}

@keyframes modal-slide-out {
  from {
    transform: translateY(0);
    opacity: 1;
  }
  to {
    transform: translateY(-20px);
    opacity: 0;
  }
}
</style>
